/**
 * The strip of six squares under a saved estimate's picture: the user's view,
 * the four corner views and (twice as wide, with the 360 mark) the 360. Dark
 * and quiet while a picture is still to come, its thumbnail once it is in,
 * outlined when it is the one shown; a pulse swells from the middle of the one rendering.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PICTURE_ORDER, type PictureId, type RenderPicture } from './types';

export interface RenderGalleryProps {
  pictures: RenderPicture[];
  activeId: PictureId | 'preview';
  /** The picture Cycles is on, for the sweep. */
  renderingId: PictureId | null;
  onPick: (id: PictureId) => void;
}

/** The 360 mark: a round arrow nearly closing on itself, "360" in the middle. */
export const Icon360: React.FC<{ className?: string }> = ({ className = 'rgal-360' }) => (
  <svg className={className} viewBox="0 0 40 40" aria-hidden="true" focusable="false">
    {/* The ring: from just past the top, clockwise round to the arrowhead at the top. */}
    <path d="M24.6 5.6 A15 15 0 1 1 11.2 7.4" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M22 1.2 L27.4 6.2 L21.6 10.4" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    <text x="20" y="23.6" textAnchor="middle" fontSize="10.5" fontWeight="800" fill="currentColor" fontFamily="inherit" letterSpacing="-0.02em">360</text>
  </svg>
);

export const RenderGallery: React.FC<RenderGalleryProps> = ({ pictures, activeId, renderingId, onPick }) => {
  const { t } = useTranslation('results');
  const byId = new Map(pictures.map((p) => [p.id, p]));
  return (
    <div className="rgal" role="group" aria-label={t('render.galleryLabel')}>
      {PICTURE_ORDER.map((id) => {
        const p = byId.get(id);
        const done = !!p?.done && !!p.url;
        const name = t(`render.pictures.${id}`);
        const cls = [
          'rgal-item',
          id === '360' ? 'rgal-item-360' : '',
          done ? 'is-done' : 'is-pending',
          activeId === id ? 'is-active' : '',
          renderingId === id && !done ? 'is-rendering' : '',
        ].filter(Boolean).join(' ');
        return (
          <button
            key={id}
            type="button"
            className={cls}
            disabled={!done}
            aria-pressed={activeId === id}
            aria-label={done ? t('render.show', { name }) : t('render.pending', { name })}
            title={done ? t('render.show', { name }) : t('render.pending', { name })}
            style={done && p?.thumbUrl ? { backgroundImage: `url("${p.thumbUrl}")` } : undefined}
            onClick={() => done && onPick(id)}
          >
            {id === '360' && <Icon360 />}
            {/* The pulse over the picture Cycles is on: rings swelling from the middle. */}
            <span className="rgal-pulse" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
};
